/**
 * Priced-work vocabulary, owned here rather than by any one feature.
 *
 * A job's Scope page and the standalone invoice builder are the same editing
 * problem — a description, a list of priced rows, and a tax rate — so they read
 * and write the same shapes. `features/jobs` re-exports these under its own
 * names (`LineItemDraft`, `JobScopeDraft`) so job code keeps its familiar
 * vocabulary while there is only one definition.
 */

/** One priced row: quantity may be fractional, money is always integer cents. */
export interface LineItemDraft {
  id: string;
  title: string;
  description: string;
  quantity: number;
  unitAmountCents: number;
}

/** A description, its priced rows, and the tax rate applied to them. */
export interface ScopeDraft {
  description: string;
  /** Tax rate in basis points (1000 = 10% GST). */
  taxRateBps: number;
  lineItems: LineItemDraft[];
}

/** A blank line item: quantity defaults to 1, money to an empty zero state. */
export function emptyLineItem(): LineItemDraft {
  return { id: crypto.randomUUID(), title: "", description: "", quantity: 1, unitAmountCents: 0 };
}

/**
 * Validation shared by every host of the editor, and deliberately light: an
 * all-optional page must never block what it is attached to. Only a row the
 * user has actually started pricing (a unit amount entered) has to be
 * coherent — titled, and with a quantity.
 *
 * @returns The first failure as a human message, or "" when the rows are fine.
 */
export function validateScopeDraft(scope: Pick<ScopeDraft, "lineItems">): string {
  for (const [index, item] of scope.lineItems.entries()) {
    if (item.unitAmountCents > 0) {
      if (!item.title.trim()) return `Give line item ${index + 1} a title, or remove it.`;
      if (!(item.quantity > 0)) return `Line item ${index + 1} needs a quantity greater than zero.`;
    }
  }
  return "";
}

/**
 * True when the editor holds something worth billing. An invoice needs at least
 * one priced row; a job's scope page is free to stay empty.
 */
export function hasPricedLineItem(scope: Pick<ScopeDraft, "lineItems">): boolean {
  return scope.lineItems.some((item) => item.unitAmountCents > 0 && item.quantity > 0);
}
